import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ProductNav from '../../components/feature/ProductNav';
import Footer from '../home/components/Footer';
import ServingCommunitySection from '../../components/feature/ServingCommunitySection';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';

const PLAN_OPTIONS = [
  {
    icon: 'ri-hand-coin-line',
    title: 'Traditional Universal Life',
    tag: 'Most Flexible',
    tagColor: 'bg-[#1A3A52] text-white',
    popular: false,
    desc: 'Flexible premiums and adjustable death benefit. Pay more when you can, less when you need to. Cash value earns a declared interest rate.',
    benefits: [
      'Flexible premium payments',
      'Adjustable death benefit',
      'Cash value earns 3–5% interest',
      'Policy loans available',
    ],
    rate: 'From $120/mo · Ages 18–65',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Indexed Universal Life',
    tag: 'Most Popular',
    tagColor: 'bg-[#D97642] text-white',
    popular: true,
    desc: 'Cash value growth linked to a market index (S&P 500) with a 0% floor — you can never lose principal. Upside potential without downside risk.',
    benefits: [
      '0% floor — never lose principal',
      'Market-linked growth potential',
      'Flexible premiums',
      'Optional income rider for retirement',
    ],
    rate: 'From $150/mo · Ages 18–60',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Guaranteed Universal Life',
    tag: 'Most Affordable',
    tagColor: 'bg-[#2D6A4F] text-white',
    popular: false,
    desc: 'Permanent coverage at the lowest possible premium. Minimal cash value focus — designed purely for lifetime death benefit protection.',
    benefits: [
      'Lowest permanent life premiums',
      'Guaranteed coverage to age 90–121',
      'No lapse guarantee',
      'Simple and predictable',
    ],
    rate: 'From $95/mo · Ages 18–75',
  },
];

const WHY_UL = [
  {
    icon: 'ri-settings-3-line',
    title: 'Maximum Flexibility',
    desc: 'Adjust your premiums and death benefit as your life changes. Perfect for business owners, growing families, or anyone with variable income.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Lifetime Protection',
    desc: 'Unlike term life, Universal Life provides permanent coverage that lasts your entire life — as long as the policy remains in force.',
  },
  {
    icon: 'ri-percent-line',
    title: 'Tax-Deferred Growth',
    desc: 'Your cash value grows tax-deferred. You only pay taxes when you withdraw — not while your money compounds inside the policy.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Policy Loans Available',
    desc: 'Borrow against your cash value tax-free for any purpose — college tuition, home improvements, or emergency expenses.',
  },
];

const COMPARISON_ROWS = [
  { feature: 'Coverage Duration', ul: 'Lifetime', term: '10–30 years', whole: 'Lifetime' },
  { feature: 'Premium Flexibility', ul: 'Flexible (adjustable)', term: 'Fixed', whole: 'Fixed' },
  { feature: 'Death Benefit', ul: 'Adjustable', term: 'Fixed', whole: 'Fixed' },
  { feature: 'Cash Value', ul: 'Yes (interest-based)', term: 'None', whole: 'Yes (guaranteed)' },
  { feature: 'Policy Loans', ul: 'Yes', term: 'No', whole: 'Yes' },
  { feature: 'Cost vs. Whole Life', ul: 'Lower', term: 'Lowest', whole: 'Higher' },
];

const FAQS = [
  {
    q: 'What is Universal Life Insurance?',
    a: 'Universal Life (UL) insurance is a type of permanent life insurance that offers flexible premiums, adjustable death benefits, and a cash value component that earns interest. Unlike whole life, you can adjust your premium payments and coverage amount as your financial needs change over time — making it especially appealing for Indiana business owners and families with variable income.',
  },
  {
    q: 'How does Universal Life differ from Whole Life insurance?',
    a: 'Universal Life offers flexible premiums and adjustable death benefits, while Whole Life has fixed premiums and guaranteed cash value growth. UL gives you more control over your policy and often costs less than whole life for the same death benefit, but Whole Life provides more predictability and locked-in guarantees. Many Indiana families start with UL for flexibility and later add a small whole life policy for guaranteed legacy planning.',
  },
  {
    q: 'Who should consider Universal Life insurance in Indiana?',
    a: 'Universal Life is ideal for Indiana families who want permanent coverage with payment flexibility, business owners with variable month-to-month income, professionals who want to adjust coverage as their mortgage balance decreases, and people seeking a balance between long-term affordability and cash value accumulation. If you need permanent insurance but find whole life premiums too rigid or expensive, UL is typically the best alternative.',
  },
  {
    q: 'How much does Universal Life insurance cost in Indiana?',
    a: 'Universal Life premiums vary based on age, health classification, coverage amount, and how much you choose to pay into the cash value. A healthy 35-year-old in Indiana might pay $150–$350 per month for $500,000 in UL coverage. The flexibility means you can pay more in high-income months to build cash value faster, or reduce to the minimum cost-of-insurance payment during tighter periods. We compare rates across 20+ carriers to find the best fit for your budget and goals.',
  },
  {
    q: 'Can my Universal Life policy lapse?',
    a: "Yes — this is the most important risk to understand with UL. If your cash value falls below zero because you paid too little for too long, the policy lapses and coverage ends. This risk increases as you age and the monthly cost of insurance rises. That is why it is critical to work with a licensed Indiana agent who can run in-force illustrations showing your policy's health over time, and alert you when you need to increase premiums to keep the policy from lapsing.",
  },
  {
    q: 'What is Indexed Universal Life (IUL)?',
    a: 'Indexed Universal Life (IUL) is a variant of universal life that links your cash value growth to a market index like the S&P 500, with a 0% floor — meaning you can never lose principal due to market drops. It offers more growth potential than traditional UL while still protecting your base balance. IUL is especially popular with Indiana families who want market-linked upside without the risk of losing cash value in a down year.',
  },
  {
    q: 'Can I increase my death benefit later?',
    a: 'Yes — most Universal Life policies allow you to increase your death benefit after issue, but you will typically need to provide evidence of insurability through medical underwriting. The younger and healthier you are when you request the increase, the more favorable the additional premium will be. Decreasing your death benefit is usually straightforward and does not require additional underwriting. Planning your coverage with room to grow is something our agents always discuss at the time of application.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Marcus & Jennifer T.',
    location: 'Carmel, IN',
    quote: 'As a business owner with variable income, Universal Life was perfect. I can pay more in good months and less in slow ones. Benjamin explained everything clearly — no pressure at all.',
    stars: 5,
  },
  {
    name: 'Patricia W.',
    location: 'Indianapolis, IN',
    quote: 'I switched from term to an IUL policy and my cash value has grown significantly. The 0% floor gives me peace of mind. Hoosier Life made the whole process simple.',
    stars: 5,
  },
  {
    name: 'David R.',
    location: 'Fishers, IN',
    quote: 'I needed permanent coverage but couldn\'t afford whole life premiums. Guaranteed UL gave me lifetime protection at a price I could manage. Highly recommend.',
    stars: 5,
  },
];

const CARRIERS = [
  'North American', 'Nationwide', 'Pacific Life', 'Protective Life', 'Transamerica', 'Lincoln Financial',
];

const SERVICE_AREAS = [
  'Indianapolis', 'Carmel', 'Fishers', 'Greenwood', 'Noblesville',
  'Westfield', 'Avon', 'Plainfield', 'Lawrence', 'Beech Grove', 'Speedway', 'Marion County',
];

const RISK_STATS = [
  { value: 'Lifetime', label: 'Coverage that never expires' },
  { value: 'Flexible', label: 'Adjust premiums as life changes' },
  { value: '0% Floor', label: 'IUL — never lose principal to markets' },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

export default function UniversalLifePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isGHLFormLoading, setIsGHLFormLoading] = useState(true);
  const [isGHLFormError, setIsGHLFormError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = 'Universal Life Insurance Indiana | Flexible Coverage';

    const setOrCreate = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    setOrCreate('description', 'Universal Life insurance in Indiana — flexible premiums, adjustable death benefits & cash value growth. Lifetime coverage that adapts to your needs. Free quotes.');
    setOrCreate('keywords', 'universal life insurance Indiana, indexed universal life, IUL Indiana, flexible life insurance, permanent life insurance Indianapolis');
    setOrCreate('last-modified', new Date().toISOString());
    setOrCreate('og:title', 'Universal Life Insurance Indiana | Flexible Permanent Coverage | Hoosier Life');
    setOrCreate('og:description', 'Universal Life insurance in Indiana — flexible premiums, adjustable death benefits, and cash value growth. Lifetime coverage that adapts to your needs.');
    setOrCreate('og:url', `${SITE_URL}/universal-life`);
    setOrCreate('og:type', 'website');
    setOrCreate('og:site_name', 'Hoosier Life Insurance');
    setOrCreate('og:image', 'https://public.readdy.ai/ai/img_res/e8b3a0b30118095d2ef196bf1bfa6afc.jpg');
    setOrCreate('twitter:card', 'summary_large_image');
    setOrCreate('twitter:title', 'Universal Life Insurance Indiana | Flexible Permanent Coverage | Hoosier Life');
    setOrCreate('twitter:description', 'Universal Life insurance in Indiana — flexible premiums, adjustable death benefits, and cash value growth.');
    setOrCreate('twitter:image', 'https://readdy.ai/api/search-image?query=professional%20life%20insurance%20consultant%20meeting%20with%20Indianapolis%20family%20discussing%20universal%20life%20insurance%20flexible%20coverage%20cash%20value%20growth%20modern%20office%20warm%20lighting%20trustworthy%20professional&width=1200&height=630&seq=og-universal-life-v1&orientation=landscape');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${SITE_URL}/universal-life`);

    // Inject schema into <head> to avoid visible JSON text in page body
    const schemaScripts: HTMLScriptElement[] = [];
    [webPageSchema, breadcrumbSchema, faqSchema, serviceSchema].forEach((schema, i) => {
      const existingScript = document.getElementById(`schema-universal-life-${i}`);
      if (existingScript) existingScript.remove();
      const script = document.createElement('script');
      script.id = `schema-universal-life-${i}`;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      schemaScripts.push(script);
    });

    return () => {
      schemaScripts.forEach(s => { try { document.head.removeChild(s); } catch (e) { /* ignore */ } });
    };
  }, []);

  useEffect(() => {
    const loadTimeout = setTimeout(() => {
      if (isGHLFormLoading) {
        setIsGHLFormError(true);
        setIsGHLFormLoading(false);
      }
    }, 10000);
    return () => clearTimeout(loadTimeout);
  }, [isGHLFormLoading]);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/universal-life#webpage`,
    url: `${SITE_URL}/universal-life`,
    name: 'Universal Life Insurance Indiana | Flexible Permanent Coverage',
    description: 'Universal Life insurance in Indiana — flexible premiums, adjustable death benefits, and cash value growth. Lifetime coverage that adapts to your needs.',
    inLanguage: 'en-US',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    datePublished: '2024-01-15T00:00:00-05:00',
    dateModified: new Date().toISOString(),
    breadcrumb: { '@id': `${SITE_URL}/universal-life#breadcrumb` },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${SITE_URL}/universal-life#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Universal Life Insurance', item: `${SITE_URL}/universal-life` },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: { '@type': 'Answer', text: faq.a },
    })),
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${SITE_URL}/universal-life#service`,
    serviceType: 'Universal Life Insurance',
    name: 'Universal Life Insurance',
    description: 'Flexible permanent life insurance with adjustable premiums and death benefits for Indiana families.',
    provider: { '@id': `${SITE_URL}/#organization` },
    areaServed: { '@type': 'State', name: 'Indiana' },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '74',
      bestRating: '5',
      worstRating: '1',
    },
    review: TESTIMONIALS.map((t) => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: t.name },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      reviewBody: t.quote,
    })),
  };

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      {/* ── Header ── */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5 group">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-shield-check-fill text-[18px] text-[#D97642]"></i>
            </div>
            <span className="text-lg font-bold text-[#1A3A52] tracking-tight whitespace-nowrap">
              Hoosier Life Insurance
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="tel:+13176482983"
              className="hidden sm:flex items-center gap-2 text-[14px] font-semibold text-[#1A3A52] hover:text-[#D97642] transition-colors whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-[16px]"></i>
              </div>
              (317) 648-2983
            </a>
            <a
              href="#quote"
              className="px-5 py-2 bg-[#D97642] text-white text-[13px] font-bold rounded-lg hover:bg-[#C26638] transition-colors whitespace-nowrap"
            >
              Get Free Quote
            </a>
          </div>
        </div>
      </header>

      {/* ── Product Navigation ── */}
      <ProductNav />

      {/* ── Breadcrumb ── */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-[1100px] mx-auto px-6 py-2.5 flex items-center gap-2 text-[13px]">
          <Link to="/" className="flex items-center gap-1.5 text-[#718096] hover:text-[#D97642] transition-colors whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-home-4-line text-[13px]"></i>
            </div>
            Home
          </Link>
          <div className="w-4 h-4 flex items-center justify-center text-gray-300">
            <i className="ri-arrow-right-s-line text-[15px]"></i>
          </div>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Universal Life Insurance</span>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden min-h-[520px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20Indiana%20family%20in%20their%2040s%20sitting%20at%20a%20bright%20modern%20kitchen%20table%20reviewing%20life%20insurance%20documents%20together%2C%20warm%20golden%20afternoon%20light%20streaming%20through%20large%20windows%2C%20cream%20and%20warm%20terracotta%20color%20palette%2C%20lush%20green%20backyard%20visible%20outside%2C%20relaxed%20confident%20expressions%2C%20professional%20lifestyle%20photography%2C%20shallow%20depth%20of%20field%2C%20soft%20bokeh%20background%2C%20trustworthy%20and%20secure%20family%20financial%20planning%20atmosphere%2C%20no%20sharp%20faces&width=1400&height=700&seq=ul-hero-indy-2025-v2&orientation=landscape"
            alt="Universal Life Insurance Indianapolis Indiana — Hoosier Life Insurance"
            title="Universal Life Insurance in Indianapolis, Indiana — Flexible Permanent Coverage"
            className="w-full h-full object-cover object-top"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1400}
            height={700}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/95 via-[#1A3A52]/85 to-[#1A3A52]/60"></div>
        </div>

        <div className="relative max-w-[1100px] mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[640px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-[12px] font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-fill text-[13px]"></i>
              </div>
              Serving Indianapolis &amp; Central Indiana
            </span>

            <h1 className="text-[36px] sm:text-[46px] md:text-[52px] font-extrabold text-white leading-[1.1] mb-6">
              Universal Life Insurance —<br />
              <span className="text-[#D97642]">Flexible Coverage for Indianapolis Families</span>
            </h1>

            <p className="text-[17px] md:text-[19px] text-white leading-[1.7] mb-8 max-w-[560px]">
              Lifetime protection with the <strong className="text-white font-bold">flexibility to adapt</strong> as your life changes. Adjust premiums, grow cash value, and keep coverage forever — from a local Indianapolis agent.
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { icon: 'ri-shield-check-fill', text: 'A-rated carriers' },
                { icon: 'ri-settings-3-fill', text: 'Flexible premiums' },
                { icon: 'ri-funds-fill', text: 'Cash value growth' },
                { icon: 'ri-heart-fill', text: '100% free, no obligation' },
              ].map((badge) => (
                <div key={badge.text} className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${badge.icon} text-[#D97642] text-[14px]`}></i>
                  </div>
                  <span className="text-[13px] text-white font-semibold whitespace-nowrap">{badge.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="#quote"
                className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all shadow-lg cursor-pointer whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-[17px]"></i>
                </div>
                Get My Free Quote
              </a>
              <a
                href="#book"
                className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-[15px] font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-check-line text-[17px]"></i>
                </div>
                Book a Free Call
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            {RISK_STATS.map((stat) => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-[28px] font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-[13px] text-white/75 leading-snug max-w-[200px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Plan Options ── */}
      <section className="bg-white py-14 md:py-18 border-b border-gray-100">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-[12px] font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Coverage Options</span>
            <h2 className="text-[26px] md:text-[32px] font-bold text-[#1A3A52] leading-tight">
              Three Universal Life Options for Every Goal
            </h2>
            <p className="text-[15px] text-[#4A5568] mt-3 max-w-[540px] mx-auto">
              Whether you want maximum flexibility, market-linked growth, or the lowest permanent premium — we have a Universal Life solution for you.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLAN_OPTIONS.map((plan) => (
              <div
                key={plan.title}
                className={`relative rounded-2xl p-7 border-2 transition-shadow hover:shadow-xl flex flex-col ${
                  plan.popular
                    ? 'border-[#D97642] bg-[#1A3A52] shadow-xl'
                    : 'border-gray-100 bg-white shadow-sm'
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#D97642] text-white text-[11px] font-bold px-4 py-1 rounded-full whitespace-nowrap">
                    Most Popular
                  </span>
                )}
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${plan.popular ? 'bg-[#D97642]/20' : 'bg-[#1A3A52]/8'}`}>
                  <i className={`${plan.icon} text-[24px] ${plan.popular ? 'text-[#D97642]' : 'text-[#1A3A52]'}`}></i>
                </div>
                <span className={`inline-block text-[11px] font-bold px-3 py-1 rounded-full mb-3 w-fit ${plan.tagColor}`}>
                  {plan.tag}
                </span>
                <h3 className={`text-[19px] font-extrabold mb-3 ${plan.popular ? 'text-white' : 'text-[#1A3A52]'}`}>
                  {plan.title}
                </h3>
                <p className={`text-[13px] leading-[1.65] mb-5 ${plan.popular ? 'text-white/75' : 'text-[#4A5568]'}`}>
                  {plan.desc}
                </p>
                <ul className="flex flex-col gap-2 mb-6 flex-1">
                  {plan.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-[14px] text-[#D97642]"></i>
                      </div>
                      <span className={`text-[13px] ${plan.popular ? 'text-white/85' : 'text-[#4A5568]'}`}>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-[13px] font-semibold mb-5 text-[#D97642]">{plan.rate}</div>
                <a
                  href="#quote"
                  className={`block text-center py-3 rounded-xl text-[14px] font-bold transition-all cursor-pointer whitespace-nowrap ${
                    plan.popular
                      ? 'bg-[#D97642] text-white hover:bg-[#C26638]'
                      : 'bg-[#1A3A52]/8 text-[#1A3A52] hover:bg-[#1A3A52] hover:text-white border border-[#1A3A52]/20'
                  }`}
                >
                  Get a Quote
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Main Content ── */}
      <section className="max-w-[1100px] mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12 xl:gap-16 items-start">

          {/* Left Column */}
          <div>

            {/* Why UL */}
            <div className="mb-14">
              <span className="text-[12px] font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Why It Matters</span>
              <h2 className="text-[26px] md:text-[32px] font-bold text-[#1A3A52] mb-8 leading-tight">
                Why Indianapolis Families Choose Universal Life
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {WHY_UL.map((item) => (
                  <div key={item.title} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#D97642]/10 mb-4">
                      <i className={`${item.icon} text-[22px] text-[#D97642]`}></i>
                    </div>
                    <h3 className="text-[15px] font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                    <p className="text-[13px] text-[#4A5568] leading-[1.65]">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Comparison Table */}
            <div className="mb-14">
              <span className="text-[12px] font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Comparison</span>
              <h2 className="text-[22px] md:text-[26px] font-bold text-[#1A3A52] mb-6 leading-tight">
                Universal Life vs. Term Life vs. Whole Life
              </h2>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="grid grid-cols-4 bg-[#1A3A52] text-white text-[13px] font-bold">
                  <div className="px-5 py-4">Feature</div>
                  <div className="px-4 py-4 text-center text-[#D97642]">Universal Life</div>
                  <div className="px-4 py-4 text-center text-white/70">Term Life</div>
                  <div className="px-4 py-4 text-center text-white/70">Whole Life</div>
                </div>
                {COMPARISON_ROWS.map((row, i) => (
                  <div
                    key={row.feature}
                    className={`grid grid-cols-4 text-[13px] border-b border-gray-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}`}
                  >
                    <div className="px-5 py-4 font-medium text-[#1A3A52]">{row.feature}</div>
                    <div className="px-4 py-4 text-center font-semibold text-[#D97642]">{row.ul}</div>
                    <div className="px-4 py-4 text-center text-[#718096]">{row.term}</div>
                    <div className="px-4 py-4 text-center text-[#718096]">{row.whole}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dark Callout */}
            <div className="bg-[#1A3A52] rounded-2xl p-8 mb-14 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-[#D97642]/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-settings-3-fill text-[20px] text-[#D97642]"></i>
                  </div>
                  <span className="text-[13px] font-semibold text-[#D97642] uppercase tracking-widest">The Flexibility Advantage</span>
                </div>
                <h3 className="text-[22px] md:text-[26px] font-bold text-white mb-4 leading-tight">
                  Life changes. Your insurance should <span className="text-[#D97642]">change with it.</span>
                </h3>
                <p className="text-[15px] text-white/80 leading-[1.7] mb-6">
                  Universal Life is the only type of permanent insurance that lets you adjust both your premiums and your death benefit over time. Had a great year in business? Pay more and build cash value faster. Going through a tough stretch? Reduce your premium temporarily. Your coverage stays in force as long as your cash value covers the cost of insurance.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <div className="text-[22px] font-extrabold text-[#D97642]">Adjust</div>
                    <div className="text-[12px] text-white/70 mt-1">Premiums up or down</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <div className="text-[22px] font-extrabold text-[#D97642]">Grow</div>
                    <div className="text-[12px] text-white/70 mt-1">Tax-deferred cash value</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <div className="text-[22px] font-extrabold text-[#D97642]">Borrow</div>
                    <div className="text-[12px] text-white/70 mt-1">Tax-free policy loans</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Carriers */}
            <div className="mb-14">
              <span className="text-[12px] font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Our Carriers</span>
              <h2 className="text-[22px] md:text-[26px] font-bold text-[#1A3A52] mb-4 leading-tight">
                We Shop Top A-Rated Universal Life Carriers
              </h2>
              <p className="text-[14px] text-[#4A5568] leading-[1.7] mb-6">
                As an independent Indianapolis insurance agency, we compare Universal Life rates across the nation's top-rated carriers — so you get the best coverage and flexibility for your situation.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {CARRIERS.map((carrier) => (
                  <div key={carrier} className="bg-white rounded-xl px-5 py-4 border border-gray-100 shadow-sm flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/8 flex-shrink-0">
                      <i className="ri-building-2-line text-[16px] text-[#1A3A52]"></i>
                    </div>
                    <span className="text-[13px] font-semibold text-[#1A3A52]">{carrier}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-[12px] font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Reviews</span>
              <h2 className="text-[26px] md:text-[32px] font-bold text-[#1A3A52] mb-8 leading-tight">
                What Indianapolis Families Are Saying
              </h2>
              <div className="flex flex-col gap-5">
                {TESTIMONIALS.map((t) => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-[#D97642] text-[14px]"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-[14px] text-[#4A5568] leading-[1.7] mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-[14px] text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-[13px] font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-[12px] text-[#718096]">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <StarRatingDisplay rating={4.9} reviewCount={74} productName="Universal Life Insurance Indiana" />
            {/* FAQ — always-visible prose for crawlers */}
            <div className="mb-12">
              <span className="text-[12px] font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Common Questions</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-2">What Indiana Families Ask About Universal Life</h2>
              <p className="text-[#4A5568] text-sm mb-8 max-w-2xl">Plain-language answers from our licensed Indiana agents — no jargon, no pressure.</p>
              <div className="space-y-7">
                <div className="border-b border-gray-100 pb-7">
                  <h3 className="text-base font-bold text-[#1A3A52] mb-2">What is Universal Life Insurance?</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed">Universal Life (UL) insurance is a type of permanent life insurance that offers flexible premiums, adjustable death benefits, and a cash value component that earns interest. Unlike whole life, you can adjust your premium payments and coverage amount as your financial needs change over time — making it especially appealing for Indiana business owners and families with variable income.</p>
                </div>
                <div className="border-b border-gray-100 pb-7">
                  <h3 className="text-base font-bold text-[#1A3A52] mb-2">How does Universal Life differ from Whole Life insurance?</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed">Universal Life offers flexible premiums and adjustable death benefits, while Whole Life has fixed premiums and guaranteed cash value growth. UL gives you more control over your policy and often costs less than whole life for the same death benefit, but Whole Life provides more predictability and locked-in guarantees. Many Indiana families start with UL for flexibility and later add a small whole life policy for guaranteed legacy planning.</p>
                </div>
                <div className="border-b border-gray-100 pb-7">
                  <h3 className="text-base font-bold text-[#1A3A52] mb-2">Who should consider Universal Life insurance in Indiana?</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed">Universal Life is ideal for Indiana families who want permanent coverage with payment flexibility, business owners with variable month-to-month income, professionals who want to adjust coverage as their mortgage balance decreases, and people seeking a balance between long-term affordability and cash value accumulation. If you need permanent insurance but find whole life premiums too rigid or expensive, UL is typically the best alternative.</p>
                </div>
                <div className="border-b border-gray-100 pb-7">
                  <h3 className="text-base font-bold text-[#1A3A52] mb-2">How much does Universal Life insurance cost in Indiana?</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed">Universal Life premiums vary based on age, health classification, coverage amount, and how much you choose to pay into the cash value. A healthy 35-year-old in Indiana might pay $150–$350 per month for $500,000 in UL coverage. The flexibility means you can pay more in high-income months to build cash value faster, or reduce to the minimum cost-of-insurance payment during tighter periods. We compare rates across 20+ carriers to find the best fit for your budget and goals.</p>
                </div>
                <div className="border-b border-gray-100 pb-7">
                  <h3 className="text-base font-bold text-[#1A3A52] mb-2">Can my Universal Life policy lapse?</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed">Yes — this is the most important risk to understand with UL. If your cash value falls below zero because you paid too little for too long, the policy lapses and coverage ends. This risk increases as you age and the monthly cost of insurance rises. That is why it is critical to work with a licensed Indiana agent who can run in-force illustrations showing your policy&apos;s health over time, and alert you when you need to increase premiums to keep the policy from lapsing.</p>
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1A3A52] mb-2">What is Indexed Universal Life (IUL)?</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed">Indexed Universal Life (IUL) is a variant of universal life that links your cash value growth to a market index like the S&P 500, with a 0% floor — meaning you can never lose principal due to market drops. It offers more growth potential than traditional UL while still protecting your base balance. IUL is especially popular with Indiana families who want market-linked upside without the risk of losing cash value in a down year.</p>
                  <div className="pt-4 border-t border-gray-100 mt-2">
                    <p className="text-sm text-[#4A5568]">
                      Not sure whether Universal Life, IUL, Term, or Whole Life is right for you?{' '}
                      <Link to="/compare" className="text-[#D97642] font-semibold hover:underline whitespace-nowrap">Compare all policy types side-by-side &rarr;</Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ — interactive accordion */}
            <div id="faq">
              <span className="text-[12px] font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-[26px] md:text-[32px] font-bold text-[#1A3A52] mb-8 leading-tight">
                Universal Life Questions — Answered
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-[14px] font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-ul-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-[18px] text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFaq === i && (
                      <div className="px-6 pb-5">
                        <p className="text-[13px] text-[#4A5568] leading-[1.75]">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Right Column — Sticky Quote Form + Booking */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">

            {/* Quote Form */}
            <div id="quote" className="bg-white rounded-lg shadow-lg p-8">
              <div className="bg-[#1A3A52] text-white p-6 rounded-t-lg -m-8 mb-6">
                <h3 className="text-2xl font-bold mb-2">Get Your Free Quote</h3>
                <p className="text-white/75">Quick, easy, and no obligation</p>
              </div>

              <div className="relative min-h-[600px]">
                {isGHLFormError ? (
                  <div className="bg-[#F9F7F4] rounded-xl p-6 border border-gray-200">
                    <div className="text-center mb-5">
                      <div className="w-14 h-14 bg-[#D97642]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-file-list-3-line text-[28px] text-[#D97642]"></i>
                      </div>
                      <h3 className="text-[16px] font-bold text-[#1A3A52] mb-2">Get Your Free Universal Life Quote</h3>
                      <p className="text-[13px] text-[#4A5568] mb-4">Connect with us to receive your personalized quote</p>
                    </div>
                    <div className="flex flex-col gap-3">
                      <a
                        href="tel:+13176482983"
                        className="flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-lg hover:bg-[#C26638] transition-all cursor-pointer"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-phone-fill text-[18px]"></i>
                        </div>
                        <div className="text-left">
                          <div className="text-[12px] opacity-90">Call for instant quote</div>
                          <div className="text-[15px] font-bold">(317) 648-2983</div>
                        </div>
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
                          if (btn) btn.click();
                        }}
                        className="flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg hover:border-[#D97642] transition-all cursor-pointer"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-chat-3-line text-[18px]"></i>
                        </div>
                        <div className="text-left">
                          <div className="text-[12px] text-[#718096]">Chat with us</div>
                          <div className="text-[14px] font-semibold">Get quote via chat</div>
                        </div>
                      </button>
                      <div className="mt-4 text-center">
                        <button
                          onClick={() => { setIsGHLFormError(false); setIsGHLFormLoading(true); }}
                          className="text-[13px] text-[#D97642] font-semibold hover:underline cursor-pointer"
                        >
                          Try loading form again
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {isGHLFormLoading && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97642]"></div>
                      </div>
                    )}
                    <iframe
                      src="https://api.leadconnectorhq.com/widget/form/l6m1l2C1ZbhWcuOBNDGB"
                      style={{ width: '100%', height: '600px', border: 'none', borderRadius: '0.5rem' }}
                      id="inline-ul-l6m1l2C1ZbhWcuOBNDGB"
                      data-layout="{'id':'INLINE'}"
                      data-trigger-type="alwaysShow"
                      data-activation-type="alwaysActivated"
                      data-deactivation-type="neverDeactivate"
                      data-form-name="Form"
                      data-height="600"
                      data-form-id="l6m1l2C1ZbhWcuOBNDGB"
                      title="Universal Life Quote Form"
                      onLoad={() => setIsGHLFormLoading(false)}
                      onError={() => { setIsGHLFormError(true); setIsGHLFormLoading(false); }}
                    />
                  </>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center text-gray-600">
                  <i className="ri-phone-line text-[#D97642] mr-3 text-xl"></i>
                  <div>
                    <p className="text-sm text-gray-500">Prefer to talk?</p>
                    <a href="tel:+13176482983" className="text-lg font-semibold text-[#D97642] hover:text-[#C26638]">
                      (317) 648-2983
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-[12px] text-[#718096] font-medium whitespace-nowrap">or book a free strategy call</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Booking Calendar */}
            <div id="book" className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-[18px] text-[#D97642]"></i>
                  </div>
                  <div>
                    <h2 className="text-[16px] font-bold text-white leading-tight">Free 15-Min Strategy Call</h2>
                    <p className="text-[12px] text-white/70">We'll walk through your options together</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Call',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerEmail: 'Email Address',
                    customerPhone: 'Phone Number',
                    notes: "Anything you'd like us to know? (Optional)",
                    submitButton: 'Confirm My Free Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                />
                <a href="tel:+13176482983" className="block text-center text-[13px] font-semibold text-[#D97642] hover:underline whitespace-nowrap mt-4">
                  Or call us: (317) 648-2983
                </a>
              </div>
            </div>

            {/* Phone CTA */}
            <div className="flex items-center justify-center gap-3 bg-white rounded-xl px-6 py-4 border border-gray-100 shadow-sm">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                <i className="ri-phone-fill text-[18px] text-[#1A3A52]"></i>
              </div>
              <div>
                <p className="text-[12px] text-[#718096]">Prefer to call us directly?</p>
                <a href="tel:+13176482983" className="text-[15px] font-bold text-[#1A3A52] hover:text-[#D97642] transition-colors whitespace-nowrap">
                  (317) 648-2983
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Service Areas ── */}
      <section className="bg-white border-t border-gray-100 py-12">
        <div className="max-w-[1100px] mx-auto px-6 text-center">
          <span className="text-[12px] font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Service Areas</span>
          <h2 className="text-[22px] md:text-[26px] font-bold text-[#1A3A52] mb-4">
            Serving All of Central Indiana
          </h2>
          <p className="text-[14px] text-[#4A5568] mb-6 max-w-[500px] mx-auto">
            We're a local Indianapolis insurance agency helping families across Marion County and surrounding communities protect what matters most.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {SERVICE_AREAS.map((city) => (
              <Link
                key={city}
                to={`/service-areas/${city.toLowerCase().replace(/\s+/g, '-')}`}
                className="group bg-white rounded-lg px-4 py-2.5 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 hover:border-[#D97642] hover:bg-[#F9F7F4] text-[#1A3A52] hover:text-[#D97642] text-[13px] font-medium cursor-pointer"
              >
                {city}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Related Services ── */}
      <section className="py-16 bg-[#F9F7F4]">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-[26px] md:text-[32px] font-bold text-[#1A3A52] mb-4">Explore More Coverage Options</h2>
            <p className="text-[15px] text-[#4A5568] max-w-2xl mx-auto">
              Discover other insurance solutions that complement your Universal Life policy
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { to: '/term-life', icon: 'ri-calendar-check-line', title: 'Term Life Insurance', desc: 'Affordable protection for a specific period with flexible coverage' },
              { to: '/whole-life', icon: 'ri-shield-check-line', title: 'Whole Life Insurance', desc: 'Lifetime coverage with guaranteed cash value growth' },
              { to: '/indexed-universal-life', icon: 'ri-line-chart-line', title: 'Indexed Universal Life', desc: 'Market-linked growth with downside protection — higher potential returns' },
              { to: '/wealth-building', icon: 'ri-funds-box-line', title: 'Wealth-Building System', desc: 'See how Universal Life fits into our 6-step wealth accumulation strategy' },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-[#D97642] hover:bg-[#F9F7F4] cursor-pointer"
              >
                <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] transition-colors">
                  <i className={`${item.icon} text-2xl text-[#1A3A52] group-hover:text-white`}></i>
                </div>
                <h3 className="text-[17px] font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                <p className="text-[#4A5568] text-[13px] mb-4">{item.desc}</p>
                <div className="flex items-center text-[#D97642] font-semibold text-[13px]">
                  Learn More <i className="ri-arrow-right-line ml-1"></i>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-[#4A5568] mb-4 text-[15px]">Still deciding which type of permanent life insurance is right for you?</p>
            <Link to="/term-vs-whole-life" className="inline-flex items-center text-[#D97642] hover:text-[#C26638] font-semibold text-[15px] transition-colors">
              <i className="ri-scales-line mr-2 text-lg"></i>
              Compare term vs. whole life insurance options →
            </Link>
          </div>
        </div>
      </section>

      {/* Serving Community Section */}
      <ServingCommunitySection productName="Universal Life Insurance" productSlug="universal-life" />

      <Footer />
    </div>
  );
}
